import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import WavesIcon from '@mui/icons-material/Waves';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import CompressIcon from '@mui/icons-material/Compress';
import SpeedIcon from '@mui/icons-material/Speed';

const ICON_COLORS = {
  heat: "#FF7043",        // orange / heat
  cool: "#4FC3F7",        // light blue
  water: "#29B6F6",       // pool / water
  time: "#BA68C8",        // timers
  power: "#FFD54F",       // electrical
  speed: "#81C784",       // motion / speed
  level: "#90A4AE",       // neutral / measurement
  error: "#FF0000",
  bluetooth: "#0a3d91",
  wifi: "#D3D3D3",
  mqtt: "#660066"
};

const SWITCH_ICONS = {
  'Flow switch': <WavesIcon sx={{ color: ICON_COLORS.water }} />,
  'High pressure switch': <SpeedIcon sx={{ color: ICON_COLORS.error }} />,
  'Low pressure switch': <SpeedIcon sx={{ color: ICON_COLORS.cool }} />,
  'Comp thermal cutout': <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.water }} />,
  'Heat exchanger cutout': <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.water }} />,
  D1: <ToggleOnOutlinedIcon />,
  D2: <ToggleOnOutlinedIcon />,
  D3: <ToggleOnOutlinedIcon />,
  'Spare 1': <ToggleOffOutlinedIcon />,
  'Spare 2': <ToggleOffOutlinedIcon />,
};

function createBaseRow(id, label) {
  return { id, label };
}

export default function HPDashboardSwitches() {
  const state = useContext(MainContext).state;
  const re = state.RE?.split(',') ?? [];

  const isV1 = re[1] === '1';

  const baseRows = isV1
    ? [
      createBaseRow(0, 'Flow switch'),
      createBaseRow(1, 'High pressure switch'),
      createBaseRow(2, 'Low pressure switch'),
      createBaseRow(3, 'Comp thermal cutout'),
      createBaseRow(4, 'Heat exchanger cutout'),
      createBaseRow(5, 'D1'),
      createBaseRow(6, 'D2'),
      createBaseRow(7, 'D3'),
    ]
    : [
      createBaseRow(0, 'Flow switch'),
      createBaseRow(1, 'High pressure switch'),
      createBaseRow(2, 'Low pressure switch'),
      createBaseRow(3, 'Spare 1'),
      createBaseRow(4, 'Spare 2'),
    ];

  const switches = baseRows.map((row, idx) => {
    const sensorIndex = 2 + idx;
    const raw = re[sensorIndex];

    if (raw == null) {
      return { ...row, status: 'Unknown', isOpen: false };
    }

    let isOpen = false;
    if (isV1) {
      isOpen = Number(raw) > 30;
    } else {
      isOpen = raw === '0';
    }

    return {
      ...row,
      status: isOpen ? 'Open' : 'Closed',
      isOpen,
    };
  });

  return (
    <>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Switches
      </Typography>

      <Grid container spacing={1.5}>
        {switches.map((sw) => {
          const icon = SWITCH_ICONS[sw.label] || <ToggleOffOutlinedIcon />;

          const card = (
            <Paper
              sx={{
                p: 1.2,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.12)', // always grey
                  color: 'rgba(255,255,255,0.7)', // icon grey
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.3,
                  mt: 0.2,
                }}
              >
                <Typography
                  color="white"
                  sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}
                  noWrap
                >
                  {sw.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 13,
                    color: sw.isOpen ? 'success.main' : 'text.disabled',
                  }}
                >
                  {sw.status}
                </Typography>
              </Box>
            </Paper>
          );

          return (
            <Grid item xs={12} key={sw.id}>
              <Tooltip title={sw.label} arrow placement="top">
                <Box>{card}</Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
